/**
 * Resolve scoring-first matchmaking from env (local dev / edge functions).
 * @returns {boolean}
 */
function resolveScoringFirstMatchmakingFlag() {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_SCORING_FIRST_MATCHMAKING === 'true') {
    return true;
  }
  try {
    if (typeof Deno !== 'undefined' && Deno.env?.get?.('MATCHMAKING_SCORING_FIRST') === 'true') {
      return true;
    }
  } catch {
    // Deno not available in this runtime
  }
  return false;
}

/**
 * Resolve variety-aware pool selection from env (local dev toggles).
 * @returns {boolean}
 */
function resolveVarietyAwarePoolSelectionFlag() {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_VARIETY_AWARE_POOL_SELECTION === 'true') {
    return true;
  }
  try {
    if (typeof Deno !== 'undefined' && Deno.env?.get?.('MATCHMAKING_VARIETY_AWARE') === 'true') {
      return true;
    }
  } catch {
    // Deno not available in this runtime
  }
  return false;
}

const MATCHMAKING_CONFIG = {
  debugLogs: false, // Set true locally when debugging matchmaking; avoids console spam in dev/prod
  
  // Feature flags for gradual rollout
  featureFlags: {
    // Doubles + normalized singles scoring / relaxation (not the same as legacySinglesMatchmaking)
    useNormalizedScoring: true,   // When false, doubles uses legacy progressive relaxation only
    compareScoringSystems: false, // Set to true to log comparison between old and new
    scoringConfig: null,          // Custom scoring config (null = use default)
    /** When true, competitive singles uses singlesCourtEngine instead of normalized singles path. Off by default. */
    legacySinglesMatchmaking: false,
    /**
     * When true, insert variety shaping between buildEligiblePool and greedy selection.
     * Adds min+1 alternates when sessionSaturationScore is elevated and ranks pool units
     * by composite fairness + marginalVarietyGain. Default false (off by default).
     *
     * Local testing:
     *   Client fill path: REACT_APP_VARIETY_AWARE_POOL_SELECTION=true (npm run start:local)
     *   Server score path: MATCHMAKING_VARIETY_AWARE=true in supabase/functions/.env
     */
    varietyAwarePoolSelection: resolveVarietyAwarePoolSelectionFlag(),
    /** Session debt model: relative wait pacing, pool expansion, conditional relaxation. Default ON. */
    sessionDebtModel: true,
    /**
     * Scoring-first matchmaking: eligibility-only pool + full-pool combo enumeration.
     * Fairness/wait/variety via normalized scoring only (no structural guarantees).
     *
     * Local testing:
     *   Client fill path: REACT_APP_SCORING_FIRST_MATCHMAKING=true
     *   Server score path: MATCHMAKING_SCORING_FIRST=true in supabase/functions/.env
     */
    scoringFirstMatchmaking: resolveScoringFirstMatchmakingFlag(),
  },
  /** Tunable weights when scoringFirstMatchmaking is enabled. */
  scoringFirst: {
    waitUrgencyWeight: 0.35,
    varietyDebtWeight: 0.25,
    recentLoadMultiplier: 10,
    exactRepeatPenalty: 15,
    exactRepeatPenaltySaturated: 45,
    novelQuartetBoostSaturated: 30,
    saturatedRepeatQuartetRate: 0.2,
    saturatedSessionScore: 0.25,
    pairSaturationWeight: 0.6,
    repeatQuartetWeight: 0.4,
  },
  // Variety-aware pool selection thresholds and weights.
  // Only active when featureFlags.varietyAwarePoolSelection is true.
  variety: {
    // sessionSaturationScore thresholds
    watchThreshold: 0.25,     // score ≥ this → include min+1 alternates in pool
    saturatedThreshold: 0.45, // score ≥ this → also include min+2; reshape before L4+ relaxation
    // Unit ranking weights (sum should equal 1.0)
    fairnessWeight: 0.6,      // weight of fairness score in unit composite ranking
    varietyWeight: 0.4,       // weight of marginalVarietyGain in unit composite ranking
    // sessionSaturationScore component weights (sum should equal 1.0)
    pairSaturationWeight: 0.6,
    repeatQuartetWeight: 0.4,
  },
  
  fairness: {
    minBonus: 50,
    minPlusOneBonus: 25,
    minPlusTwoBonus: 10,
    gamesAboveMinPenalty: 10,
  },
  penalties: {
    partnershipEmergency: 10000,
    repeatTeamBase: 5000,
    categoryGap1: 2000,  // Small penalty for +1 gap (e.g., D and C)
    categoryGap2: 5000,  // Bigger penalty for +2 gap (e.g., D and B)
    categoryGapHardBlock: 3, // Hard block at gap >= this value (default 3 = block A-D combinations)
    // +3 gap (e.g., A and D) is a hard block by default
    unbalancedMensVsLadies: -500,
    unbalancedMixedMismatch: -200,
    // Unified interaction penalty system - percentage-based only
    // All penalties calculated based on FUTURE state (after this game would be played)
    // Simplified to use just two thresholds: low (small penalty) and high (heavy penalty)
    interactions: {
      // Same-team interaction penalties (playing WITH the same person)
      sameTeam: {
        lowThreshold: 20,    // Percentage threshold for low penalty
        highThreshold: 25,   // Percentage threshold for high penalty (almost hard block)
        lowPenalty: 1000,    // Small penalty when >= lowThreshold
        highPenalty: 30000,  // Heavy penalty when >= highThreshold
      },
      // Total interaction penalties (any interaction type: same-team OR opponent)
      total: {
        lowThreshold: 20,    // Percentage threshold for low penalty
        highThreshold: 50,   // Percentage threshold for high penalty (almost hard block)
        lowPenalty: 500,     // Small penalty when >= lowThreshold
        highPenalty: 30000,  // Heavy penalty when >= highThreshold
        // Interaction density check: if interactions >= games - 1, that's way too high
        densityThreshold: 30000, // Same as highPenalty for extreme density
      },
      // Consecutive interaction penalties (recent consecutive games)
      // Heavily penalizes when players interact in consecutive games to prevent perception of always playing together
      consecutive: {
        sameTeam2: 15000,    // 2 consecutive same-team interactions: Heavy penalty
        sameTeam3Plus: 40000, // 3+ consecutive same-team interactions: Very heavy penalty (almost hard block)
        total2: 10000,       // 2 consecutive total interactions: Heavy penalty
        total3Plus: 35000,   // 3+ consecutive total interactions: Very heavy penalty (almost hard block)
      },
    },
  },
  bonuses: {
    balancedGameType: 1000,
    cooldownPerPlayer: 500,
  },
  elo: {
    teamBalanceBase: 1000,
    teamBalanceMultiplier: 30,
    crossCourtBase: 800,
    crossCourtMultiplier: 4,
    individualBase: 300,
    individualMultiplier: 2,
  },
  waitGuarantee: {
    // Fallback when session court count not available. Live sessions use ceil(numberOfCourts × 1.5) when sessionDebtModel is on.
    courtsObservedThreshold: 4,
    waitGuaranteeMultiplier: 1.5,
  },
  /**
   * Max waitlist players scored for one fill. `slotFill` keeps the first N by
   * `waitlist_position`. Stops scoring-first C(n,4) from hitting Edge Function CPU 546
   * when a full session is still waiting (empty-court Create Game at session start).
   */
  waitlistSearchCap: 20,
  sessionDebt: {
    enabled: true,
    recentWindowGames: 8,
    courtsPerGameAhead: 1.5,
    waitGuaranteeMultiplier: 1.5,
    minDistinctQuartets: 3,
    weights: {
      relative: 1.0,
      urgency: 0.8,
      game: 0.6,
      recent: 0.5,
      variety: 0.4,
    },
  },
  preferences: {
    violationPenalty: 8000, // Scoring penalty per player whose soft preference is violated
  },
  hardPreference: {
    // Courts observed threshold before showing the "waiting due to hard preference" warning badge
    longWaitThreshold: 4,
  },
  consecutiveInteractionBlock: {
    enabled: true,
    // Block when either player already shared their last N personal games with the other.
    // threshold 2 = history streak >= 2 → block the 3rd consecutive personal game together.
    threshold: 2,
  },
  // Block same pair teaming again until each has played more than repeatTeamWindow games since they last played together (per-player window; partnerships exempt)
  repeatTeamWindow: 6, // was 3 — larger window to avoid repeat pairs (e.g. same pair in games 9 and 16)
  // Game-type deficit boost — applied to the final score (outside weighted dimensions) so an
  // under-represented type can win over a slightly higher-scoring combo of a different type.
  gameTypeBalance: {
    enabled: true,
    // Court-hint / priority relaxation
    deficitThreshold: 0.75,          // games behind target before we activate the priority hint
    // Early session: small samples make capacity-normalised deficits noisy. Gate the aggressive
    // court-hint loop (levels 0–5 type filter) separately from the soft score boost.
    minSessionDoublesGamesForCourtHint: 10,
    minSessionDoublesGamesForDeficitBoost: 6,
    priorityRelaxationMaxLevel: 5,   // relaxation levels tried for the hinted type before giving up
    minPlayersForLevelsCourt: 4,     // minimum same-gender players needed to form a mens/womens court
    minPlayersPerGenderForMixed: 2,  // minimum players per gender needed to form a mixed court
    // Score boost
    deficitBoostPerGameBehind: 0.8,  // final-score points added per game this type trails second-lowest
    deficitBoostCap: 15,             // maximum additive boost to prevent overwhelming other factors
    // Deprecated — superseded by minPlayersForLevelsCourt; kept for backward compat
    womensMinFemaleCount: 4,
  },
};

const RELAXATION_LEVELS = {
  STRICT: 1,
  ALLOW_REPEAT_TEAMS: 2,
};

const debugLog = (...args) => {
  if (MATCHMAKING_CONFIG.debugLogs) {
    console.log(...args);
  }
};

const setMatchmakingDebug = enabled => {
  MATCHMAKING_CONFIG.debugLogs = Boolean(enabled);
};

export {
  MATCHMAKING_CONFIG,
  RELAXATION_LEVELS,
  debugLog,
  setMatchmakingDebug,
};

// Export threshold for convenience
export const WAIT_GUARANTEE_THRESHOLD = MATCHMAKING_CONFIG.waitGuarantee.courtsObservedThreshold;

